import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { LocaleContext } from 'providers/locale-settings-context';
import CargomaticDatePicker from './CargomaticDatePicker';
import TimePicker from './TimePicker';
import useDateTimeField from './hooks/useDateTimeField';

/**
 * Composed date + (optional) time field. The consumer passes a single `value`
 * (full datetime) and `onChange`, and gets the merged value back — all the
 * masked-input draft handling and date/time merging lives in useDateTimeField,
 * so callers don't reimplement it (and can't reintroduce the snap-back bug).
 *
 * Set `showTime={false}` for a date-only field.
 */
export default function DateTimeField({
  value,
  onChange,
  timezone,
  showTime,
  disabled,
  isClearable,
  dateFormat,
  placeholderText,
  timeIntervals,
  className,
  dateTestId,
  timeTestId,
}) {
  const localeContext = useContext(LocaleContext);
  const is24Hour = !!localeContext?.localeData?.is24HourFormat;
  const { dateValue, timeText, handleDateChange, handleTimeChange } =
    useDateTimeField({ value, onChange, timezone, is24Hour });

  return (
    <div className={classnames('flex flex-wrap items-center gap-2', className)}>
      <CargomaticDatePicker
        dataTestId={dateTestId}
        selected={dateValue}
        onChange={handleDateChange}
        dateFormat={dateFormat}
        placeholderText={placeholderText}
        isClearable={isClearable}
        disabled={disabled}
      />
      {showTime && (
        <TimePicker
          dataTestId={timeTestId}
          value={timeText}
          onChange={handleTimeChange}
          disabled={disabled}
          is24HourClock={is24Hour}
          timeIntervals={timeIntervals}
        />
      )}
    </div>
  );
}

DateTimeField.propTypes = {
  // Full datetime value owned by the consumer.
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.object, // moment
    PropTypes.string,
  ]),
  // Receives the merged value: a tz-aware moment when `timezone` is set,
  // otherwise a Date (or null when cleared).
  onChange: PropTypes.func.isRequired,
  timezone: PropTypes.string,
  showTime: PropTypes.bool,
  disabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  dateFormat: PropTypes.string,
  placeholderText: PropTypes.string,
  timeIntervals: PropTypes.number,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  dateTestId: PropTypes.string,
  timeTestId: PropTypes.string,
};

DateTimeField.defaultProps = {
  showTime: true,
  isClearable: true,
  disabled: false,
};
